import React from 'react';
import { safeStorageImageUrl } from '../../restaurantShowcase';

export type CustomerAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

interface CustomerAvatarProps {
    name: string;
    avatarUrl?: string | null;
    size?: CustomerAvatarSize;
    verified?: boolean;
    onClick?: () => void;
    className?: string;
}

const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '??';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFromName = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const palette = ['bg-brand-500', 'bg-peach-600', 'bg-wine-500', 'bg-pink-500', 'bg-success', 'bg-warning', 'bg-info'];
    return palette[Math.abs(hash) % palette.length];
};

const SIZE_MAP: Record<CustomerAvatarSize, { box: string; text: string; badge: number; badgeOffset: string }> = {
    xs: { box: 'h-8 w-8',   text: 'text-xs', badge: 14, badgeOffset: '-end-0.5 -top-0.5' },
    sm: { box: 'h-10 w-10', text: 'text-xs',     badge: 16, badgeOffset: '-end-1 -top-1' },
    md: { box: 'h-12 w-12', text: 'text-sm',     badge: 18, badgeOffset: '-end-1 -top-1' },
    lg: { box: 'h-14 w-14', text: 'text-base',   badge: 22, badgeOffset: '-end-1.5 -top-1.5' },
};

const VerifiedBadge: React.FC<{ size: number }> = ({ size }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill="#ffffff"
            d="M3.046 8.277A4.402 4.402 0 0 1 8.303 3.03a4.4 4.4 0 0 1 7.411 0 4.397 4.397 0 0 1 5.19 3.068c.207.713.23 1.466.067 2.19a4.4 4.4 0 0 1 0 7.415 4.403 4.403 0 0 1-3.06 5.187 4.398 4.398 0 0 1-2.186.072 4.398 4.398 0 0 1-7.422 0 4.398 4.398 0 0 1-5.257-5.248 4.4 4.4 0 0 1 0-7.437Z"
        />
        <path
            fill="#A62829"
            d="M4.674 8.954a3.602 3.602 0 0 1 4.301-4.293 3.6 3.6 0 0 1 6.064 0 3.598 3.598 0 0 1 4.3 4.302 3.6 3.6 0 0 1 0 6.067 3.6 3.6 0 0 1-4.29 4.302 3.6 3.6 0 0 1-6.074 0 3.598 3.598 0 0 1-4.3-4.293 3.6 3.6 0 0 1 0-6.085Z"
        />
        <path
            fill="#ffffff"
            d="M15.707 9.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 12.586l3.293-3.293a1 1 0 0 1 1.414 0Z"
        />
    </svg>
);

export const CustomerAvatar: React.FC<CustomerAvatarProps> = ({
    name,
    avatarUrl,
    size = 'sm',
    verified = false,
    onClick,
    className = '',
}) => {
    const dims = SIZE_MAP[size];
    const isInteractive = Boolean(onClick);
    // Image introuvable → on retombe sur les initiales plutôt qu'une image cassée.
    const [broken, setBroken] = React.useState(false);

    // Photo affichée seulement si elle vient de notre Storage : une image tierce chargée
    // depuis le tableau de bord exposerait le restaurateur (IP, pistage) à ce tiers.
    const safeUrl = safeStorageImageUrl(avatarUrl);
    const inner = safeUrl && !broken ? (
        <img
            className={`${dims.box} rounded-full object-cover`}
            src={safeUrl}
            alt={name}
            onError={() => setBroken(true)}
        />
    ) : (
        <div aria-hidden="true" className={`${dims.box} ${colorFromName(name)} rounded-full text-white font-bold flex items-center justify-center ${dims.text}`}>
            {initialsOf(name)}
        </div>
    );

    const content = (
        <div className="relative inline-block">
            {inner}
            {verified && (
                <span className={`absolute ${dims.badgeOffset} drop-shadow-sm pointer-events-none`}>
                    <span className="sr-only">Client vérifié</span>
                    <VerifiedBadge size={dims.badge} />
                </span>
            )}
        </div>
    );

    if (!isInteractive) {
        return <div className={className}>{content}</div>;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative rounded-full transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 ${className}`}
            aria-label={`Voir la fiche de ${name}`}
        >
            {content}
        </button>
    );
};

export default CustomerAvatar;
